import { flexRender, Row, Table } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { cn } from '@/utils/classnames';
import { format } from 'date-fns';
import _range from 'lodash/range';
import _startCase from 'lodash/startCase';
import {
  TimerReset,
  ChevronDown,
  ChevronUp,
  Hourglass,
  XCircle,
} from 'lucide-react';
import { Fragment, PropsWithChildren, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Typography } from '@/components/ui/typography';

import EmptyCampaigns from '@/assets/icons/empty-campaigns.svg';
import UserCheck from '@/assets/icons/user-check.svg';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { REACH } from '@/types/channels';

import { BudgetRecommendation } from './budget-recommendation';
import { CampaignDataUpdateBanner } from './campaign-data-update-banner';
import { CampaignRowLayout } from './campaign-row-layout';
import { ChannelCannotBePausedBanner } from './channel-cannot-be-paused-banner';
import { ReachChannels } from './reach-channels';
import { getCampaignWindowAt } from './time-zone-window';

interface CardsProps {
  table: Table<ActiveSourcingChannelDetails>;
  /** The opening's IANA zone, when metrics count its local days. */
  timeZone?: string;
}

export function Cards({ table, timeZone }: CardsProps) {
  if (table.getRowModel().rows?.length <= 0) return <CardsEmpty />;

  return (
    <>
      {table.getRowModel().rows.map(row => (
        <CardContent key={row.id} row={row} timeZone={timeZone} />
      ))}
    </>
  );
}

function CardContent({
  row,
  timeZone,
}: {
  row: Row<ActiveSourcingChannelDetails>;
  timeZone?: string;
}) {
  const intl = useIntl();
  const [isReachExpanded, setIsReachExpanded] = useState(false);

  const channelSlug = row.original.sourcing_channel.slug;
  const isScheduled = row.original.status === 'scheduled';
  // Behind the flag the go-live moment reads at the opening, with its time.
  const goLive = timeZone
    ? getCampaignWindowAt(row.original.starts_at, null, timeZone)
    : null;
  const isPublishing = row.original.status === 'in_progress';
  const isFailed = row.original.status === 'failed';
  const cells = row.getVisibleCells();
  const actionsCell = cells.find(c => c.column.id === 'actions');
  const channelCell = cells.find(c => c.column.id === 'sourcing_channel');
  const allMetricCells = cells.filter(
    c => c.column.id !== 'actions' && c.column.id !== 'sourcing_channel',
  );

  return (
    <div className={cn('relative', row.original.managed_by && 'mt-3')}>
      {row.original.managed_by && (
        <Tag
          icon={UserCheck}
          text={_startCase(row.original.managed_by)}
          className="bg-fx-teal-50 absolute -top-3 left-3"
        />
      )}
      <RowCard>
        <CampaignRowLayout
          actionsCell={actionsCell}
          channelCell={channelCell}
          metricCells={allMetricCells}
          actionsPanelClassName="bg-fx-neutral-50 gap-6"
          metricDividerClassName="top-4 bottom-4"
          channelExtra={
            channelSlug === REACH && (
              <Button
                variant="link"
                size="sm"
                className="hover:no-underline"
                onClick={() => setIsReachExpanded(!isReachExpanded)}
              >
                {intl.formatMessage({
                  defaultMessage: 'Channels',
                  description:
                    'Link on Reach card to expand nested publisher sub-rows',
                })}
                {isReachExpanded ? <ChevronUp /> : <ChevronDown />}
              </Button>
            )
          }
          renderMetricCell={(cell, i) => {
            if ((isScheduled || isPublishing || isFailed) && i >= 2)
              return null;
            return flexRender(cell.column.columnDef.cell, cell.getContext());
          }}
          metricGridExtra={
            <>
              {/* Scheduled info spans the remaining columns (3 → end) */}
              {isScheduled && (
                <div
                  className="relative flex items-center gap-3 px-6 py-3"
                  style={{ gridColumn: `3 / ${allMetricCells.length + 1}` }}
                >
                  <div className="bg-fx-neutral-200 absolute top-4 bottom-4 left-0 w-px" />
                  <TimerReset className="text-fx-neutral-600 size-4 shrink-0" />
                  <Typography variant="body1" className="!text-fx-neutral-600">
                    <FormattedMessage
                      defaultMessage="Goes live on <b>{date}</b> — Campaign is scheduled and will start automatically"
                      description="Scheduled campaign info: shows the future start date of the campaign"
                      values={{
                        date: goLive
                          ? `${goLive.startDayLong}, ${goLive.startTime} ${goLive.startAbbr}`
                          : format(row.original.starts_at, 'do MMMM'),
                        b: (chunks: React.ReactNode) => (
                          <strong className="font-semibold">{chunks}</strong>
                        ),
                      }}
                    />
                  </Typography>
                </div>
              )}

              {/* Publishing info spans the remaining columns (3 → end) */}
              {isPublishing && (
                <div
                  className="relative flex items-center gap-3 px-6 py-3"
                  style={{ gridColumn: `3 / ${allMetricCells.length + 1}` }}
                >
                  <div className="bg-fx-neutral-200 absolute top-4 bottom-4 left-0 w-px" />
                  <Hourglass className="text-fx-neutral-600 size-4 shrink-0" />
                  <Typography variant="body1" className="!text-fx-neutral-600">
                    <FormattedMessage
                      defaultMessage="Submitted to <b>{channelName}</b>. Goes live once the board confirms - usually within <b>{processingTime} hours</b>."
                      description="Publishing campaign info: campaign has been submitted to the job board and is awaiting confirmation"
                      values={{
                        channelName: row.original.sourcing_channel.name,
                        processingTime:
                          row.original.time_to_process ??
                          intl.formatMessage({
                            defaultMessage: 'a few',
                            description:
                              'Default processing time for a campaign when not set',
                          }),
                        b: (chunks: React.ReactNode) => (
                          <strong className="font-semibold">{chunks}</strong>
                        ),
                      }}
                    />
                  </Typography>
                </div>
              )}

              {/* Failed info spans the remaining columns (3 → end) */}
              {isFailed && (
                <div
                  className="relative flex items-center gap-3 px-6 py-3"
                  style={{ gridColumn: `3 / ${allMetricCells.length + 1}` }}
                >
                  <div className="bg-fx-neutral-200 absolute top-4 bottom-4 left-0 w-px" />
                  <XCircle className="text-fx-rose size-4 shrink-0" />
                  <Typography variant="body1" className="!text-fx-rose">
                    {row.original.error ? (
                      <FormattedMessage
                        defaultMessage="Failed to post job on <b>{channelName}</b>. Error: {error}"
                        description="Failed campaign info: campaign failed to post to the job board, shows the channel name and error reason"
                        values={{
                          channelName: row.original.sourcing_channel.name,
                          error: row.original.error,
                          b: (chunks: React.ReactNode) => (
                            <strong className="font-semibold">{chunks}</strong>
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="Failed to post job on <b>{channelName}</b>."
                        description="Failed campaign info: campaign failed to post to the job board, shows the channel name"
                        values={{
                          channelName: row.original.sourcing_channel.name,
                          b: (chunks: React.ReactNode) => (
                            <strong className="font-semibold">{chunks}</strong>
                          ),
                        }}
                      />
                    )}
                  </Typography>
                </div>
              )}
            </>
          }
        />

        {/* Reach publishers when expanded */}
        {isReachExpanded &&
          row.original.sourcing_purchase_id &&
          channelSlug === REACH && (
            <ReachChannels
              sourcingPurchaseId={row.original.sourcing_purchase_id}
            />
          )}

        {/* Increase/decrease budget recommendation */}
        <BudgetRecommendation channel={row.original} />

        {/* Channel cannot be paused banner for Boost and ZipRecruiter */}
        <ChannelCannotBePausedBanner
          channelSlug={channelSlug}
          status={row.original.status}
        />
        {/* Add end date banner */}
        <CampaignDataUpdateBanner campaignData={row.original} />
      </RowCard>
    </div>
  );
}

function RowCard({ children }: PropsWithChildren) {
  return (
    <div className="border-fx-neutral-200 flex flex-col overflow-clip rounded-lg border bg-white shadow-sm transition-colors">
      {children}
    </div>
  );
}

function CardsEmpty() {
  const intl = useIntl();

  return (
    <div className="border-fx-neutral-200 rounded-lg border bg-white pb-6 text-center">
      <EmptyState
        icon={EmptyCampaigns}
        header={intl.formatMessage({
          defaultMessage: 'No campaigns to show',
          description:
            'Empty state when the opening has no paid channel campaigns',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'There are no campaigns for this opening to show at the moment',
          description:
            'Reassure user: table is empty because nothing is set up yet',
        })}
      />
    </div>
  );
}

// The metric column that shows the campaign's end at the opening under its days.
const DURATION_COLUMN_INDEX = 1;

Cards.Skeleton = function CardsSkeleton() {
  const localDays = useLocalDateRanges();

  return (
    <>
      {_range(2).map(rowIndex => (
        <RowCard key={rowIndex}>
          <div className="flex items-stretch">
            {/* Left actions panel */}
            <div className="bg-fx-neutral-50 border-fx-neutral-200 flex shrink-0 flex-col items-center justify-center gap-6 self-stretch border-r p-4">
              <Skeleton className="h-4 w-8 rounded-full" />
              <Skeleton className="h-4 w-4 rounded" />
            </div>

            {/* Main content */}
            <div className="flex min-w-0 flex-1 items-center gap-6 self-stretch px-4">
              {/* Channel info */}
              <div className="flex w-[216px] shrink-0 flex-col gap-2 py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <Skeleton className="h-4 w-full" />
              </div>

              {/* Metric columns with dividers */}
              {_range(6).map(colIndex => (
                <Fragment key={colIndex}>
                  <div className="bg-fx-neutral-200 h-20 w-px shrink-0" />
                  <div className="flex min-w-[80px] flex-1 flex-col items-center gap-2 py-3">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-6 w-12" />
                    {localDays && colIndex === DURATION_COLUMN_INDEX && (
                      <>
                        <Skeleton className="h-3 w-14" />
                        <Skeleton className="h-3 w-16" />
                      </>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </RowCard>
      ))}
    </>
  );
};
